// Small form controls shared by the radar tabs.
import type { ReactNode } from 'react';

const INPUT = 'mt-1 block w-full px-3 py-2 border border-border-strong rounded-md text-sm bg-surface text-content disabled:opacity-50';

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block text-xs text-content-muted ${className}`}>
      {label}
      {children}
    </label>
  );
}

export function TextInput({ label, value, onChange, placeholder, type = 'text', min, max, step, disabled }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
  type?: 'text' | 'search' | 'number'; min?: number; max?: number; step?: number; disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <input
        type={type} value={value} placeholder={placeholder} min={min} max={max} step={step} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} className={INPUT}
      />
    </Field>
  );
}

export function SelectInput<T extends string>({ label, value, onChange, options, all, disabled }: {
  label: string; value: T; onChange: (v: T) => void;
  /** [value, label] pairs, or plain values shown as they are. */
  options: readonly (readonly [string, string] | string)[];
  /** Label of the empty "no filter" option; omitted means no such option. */
  all?: string;
  disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as T)} className={INPUT}>
        {all !== undefined && <option value="">{all}</option>}
        {options.map((o) => {
          const [v, l] = typeof o === 'string' ? [o, o] : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </Field>
  );
}

export function Pager({ page, pages, onPage, note }: { page: number; pages: number; onPage: (p: number) => void; note?: string }) {
  return (
    <nav className="flex flex-wrap items-center justify-end gap-3 text-sm text-content-muted">
      <button type="button" className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)}>← Previous</button>
      <span>Page {page} / {pages}{note ? ` · ${note}` : ''}</span>
      <button type="button" className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next →</button>
    </nav>
  );
}

export function Loading({ what }: { what: string }) {
  return <p className="text-sm text-content-subtle">Loading {what}…</p>;
}

export function LoadError({ error }: { error: unknown }) {
  return <p className="text-sm text-danger">{(error as Error)?.message || 'Failed to load data'}</p>;
}
